import Vue from 'vue'
import {substringIndex} from '@utils'

export default class Storage
{
    constructor(data = {}) {

        const methods = [
            ...Object.getOwnPropertyNames(Object.getPrototypeOf(this)),
            ...Object.getOwnPropertyNames(Object.getPrototypeOf(Object.getPrototypeOf(this)))
        ].filter((value, index, self) => self.indexOf(value) === index )

        this.$__methods__ = methods
        this.setMultiple(data)
        this.init()
    }

    init() {
        // can be used to apply constructor logic
    }

    get(key, otherwise = undefined) {
        return (undefined !== this[key]) ? this[key] : otherwise
    }

    set(key, value) {
        // check for duplicates with Form class methods
        if( inArray(key, this.$__methods__) ) {
            throw Error(`Data parameter: "${key}" is not allowed. Data parameter name on first level cannot be: [${this.$__methods__.join(', ')}]`)
        }

        Vue.set(this, key, value)
        this._registerAttribute(key)
    }

    setMultiple(data) {
        this.clear()

        for(let key in data) {
            this.set(key, data[key])
        }

        return this
    }

    has(key) {
        return this.hasOwnProperty(key)
    }

    /**
     * Used to replace a value
     *
     * @param  {String} key   Field name that we need to replace
     * @param  {Mixed}  data  Cloned value
     */
    replace( key, data ) {
        this.remove(key)
        this.set(key, data)
    }

    /**
     * Remove specific key(s) from Storage
     *
     * @param  {String|Function} key You can give a string for example: "my.special.key" or
     *                               a function that filters for example the keys that starts with:
     *                               ( key ) => key.indexOf('my.special') === 0
     * @return {Boolean}
     */
    remove(key) {
        if( typeOf(key) === 'function' ) {
            let matches = this.keys().filter(key)

            for(let target of matches) {
                this.remove(target)
            }

            return true
        }

        if( this.has(key) ) {
            Vue.delete(this, key)
            this._unregisterAttribute(key)
        }

        return true
    }

    any() {
        return this.count() > 0
    }

    count() {
        return this.keys().length
    }

    clear() {
        this.keys().slice().forEach( key => {
            this.remove(key)
        })
    }

    all() {
        let data = {}
        this.keys().forEach( key => {
            data[key] = this[key]
        })
        return data
    }

    keys() {
        if( ! Array.isArray(this._attributes))
            this._attributes = []

        return this._attributes
    }

    setDeep(key, value, force = true)
    {
        let keys = key.split('.')
        let lastKey = keys[keys.length-1]

        keys.reduce(
            (object, key, index) => {
                if(key === lastKey) {
                    if( false === force && undefined !== object[key] ) {
                        return object[key]
                    }

                    // if it is just one key (without deep) we need to register it in the _attributes
                    if( index === 0 ) {
                        this.set(key, value)
                        return
                    }

                    Vue.set(object, key, value)
                    return
                }
                else if ( key === keys[0] && undefined === object[key] ) {
                    // if it is the first key we need to register it in the _attributes
                    this.set(key, {})
                }
                else if( undefined === object[key] ) {
                    Vue.set(object, key, {})
                }

                return object[key]
            },
            this
        )
    }

    hasDeep(key) {
        return (undefined !== this.getDeep(key, undefined))
    }

    getDeep(key, otherwise = undefined) {
        let result = key.split('.').reduce(
            (object, index) => {
                if( typeof object === 'undefined' ) {
                    return
                }

                return object[index]
            },
            this.all()
        )

        return (undefined !== result) ? result : otherwise
    }

    getDeepParent(key, otherwise = undefined, parentLevel = -1) {
        let parentKey = substringIndex(key, '.', parentLevel)
        return this.getDeep(parentKey, otherwise)
    }

    //////////////////////////////////////////////

    _registerAttribute(key) {
        // register storage keys so to be distinguishable from other object props
        if( this._attributes.indexOf( key ) === -1 )
            this._attributes.push(key)
    }

    _unregisterAttribute(key) {
        this._attributes.splice(this._attributes.indexOf(key), 1)
    }
}
